import { z } from 'zod';
import type { WorkCalendarToken, WorkDayToken, ExceptionDayToken } from '@sleed/bizcommerce-api';

export const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
export const DAY_TYPES = ['Working', 'NonWorking'] as const;

// ── Work calendar (master) ───────────────────────────────────────────────────
export const workCalendarSchema = z.object({
	id: z.number().optional(),
	year: z.number({ message: 'validation.required' }).int(),
	wlmt: z.boolean(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int()
});
export type WorkCalendarFormValues = z.infer<typeof workCalendarSchema>;
export const EMPTY_WORK_CALENDAR: WorkCalendarFormValues = { year: new Date().getFullYear(), wlmt: false, iv: true, si: 0 };

export function calendarToForm(t: WorkCalendarToken): WorkCalendarFormValues {
	return { id: t.id, year: t.year ?? new Date().getFullYear(), wlmt: t.wlmt ?? false, iv: t.iv ?? true, si: t.si ?? 0 };
}
export function calendarToToken(f: WorkCalendarFormValues): WorkCalendarToken {
	return { id: f.id, year: f.year, wlmt: f.wlmt, iv: f.iv, si: f.si };
}

// ── Shared day-shift fields (work days + exception days) ──────────────────────
const shiftFields = {
	dayType: z.enum(DAY_TYPES),
	startTime: z.string().nullable(),
	endTime: z.string().nullable(),
	startTime2: z.string().nullable(),
	endTime2: z.string().nullable(),
	cutOffTime: z.string().nullable(),
	fc: z.number().nullable()
};

// ── Work day (detail) ─────────────────────────────────────────────────────────
export const workDaySchema = z.object({
	id: z.number().optional(),
	wcId: z.number(),
	day: z.enum(DAYS_OF_WEEK),
	...shiftFields
});
export type WorkDayFormValues = z.infer<typeof workDaySchema>;
export const emptyWorkDay = (wcId: number): WorkDayFormValues => ({
	wcId,
	day: 'Monday',
	dayType: 'Working',
	startTime: null,
	endTime: null,
	startTime2: null,
	endTime2: null,
	cutOffTime: null,
	fc: null
});
export function workDayToForm(t: WorkDayToken): WorkDayFormValues {
	return {
		id: t.id,
		wcId: t.wcId ?? 0,
		day: (DAYS_OF_WEEK.includes(t.day as never) ? t.day : 'Monday') as WorkDayFormValues['day'],
		dayType: (t.dayType === 'NonWorking' ? 'NonWorking' : 'Working') as WorkDayFormValues['dayType'],
		startTime: t.startTime ?? null,
		endTime: t.endTime ?? null,
		startTime2: t.startTime2 ?? null,
		endTime2: t.endTime2 ?? null,
		cutOffTime: t.cutOffTime ?? null,
		fc: t.fc ?? null
	};
}
export function workDayToToken(f: WorkDayFormValues): WorkDayToken {
	return { ...f };
}

// ── Exception day (detail) ──────────────────────────────────────────────────
export const exceptionDaySchema = z.object({
	id: z.number().optional(),
	wcId: z.number(),
	date: z.string().min(1, 'validation.required'),
	...shiftFields
});
export type ExceptionDayFormValues = z.infer<typeof exceptionDaySchema>;
export const emptyExceptionDay = (wcId: number): ExceptionDayFormValues => ({
	wcId,
	date: '',
	dayType: 'NonWorking',
	startTime: null,
	endTime: null,
	startTime2: null,
	endTime2: null,
	cutOffTime: null,
	fc: null
});
export function exceptionDayToForm(t: ExceptionDayToken): ExceptionDayFormValues {
	return {
		id: t.id,
		wcId: t.wcId ?? 0,
		date: t.date ? String(t.date).slice(0, 10) : '',
		dayType: (t.dayType === 'Working' ? 'Working' : 'NonWorking') as ExceptionDayFormValues['dayType'],
		startTime: t.startTime ?? null,
		endTime: t.endTime ?? null,
		startTime2: t.startTime2 ?? null,
		endTime2: t.endTime2 ?? null,
		cutOffTime: t.cutOffTime ?? null,
		fc: t.fc ?? null
	};
}
export function exceptionDayToToken(f: ExceptionDayFormValues): ExceptionDayToken {
	return { ...f };
}
